/**
 * 작성자 : 김광민
 * 날짜 : 2024.03.14
 * 경로 : 대시보드 & 상황판 > 터널 종합 진행 현황 (일반)
 */

import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { ERROR } from '../../_constants';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import PlayButton from './PlayButton';
import RefreshButton from './RefreshButton';
import { useRecoilValue } from 'recoil';
import { IUser, tInfoUpdateState, userState } from '../../atoms';
import { apiGet } from '../../services/_common';
import TunnelTable from '../tunnel/TunnelTable';
import SkeletonTable from '../SkeletonTable';
import IssueGuide from '../IssueGuide';

type Props = {
  visibleRefreshBtn?: boolean;
  uniqueKey: string;
  refetchInterval?: number;
};

const TunnelTotalTable = ({ visibleRefreshBtn = true, uniqueKey, refetchInterval }: Props) => {
  const { t } = useTranslation();
  const { hCd, sCd } = useRecoilValue(userState);
  const [autoPlay, setAutoPlay] = useState(visibleRefreshBtn === false);
  const isTunnelInfoUpdated = useRecoilValue(tInfoUpdateState);

  // Tarea 데이터 요청 함수
  const fetchTareaData = async () => {
    try {
      const req = { hCd, sCd };
      const res = await apiGet({ path: `/tarea/t/total`, req });
      const { data } = res.data || {};
      return data?.tareaList || [];
    } catch (error) {
      console.error('API 호출 중 오류 발생:', error);
      toast.error(t(ERROR));
      throw new Error('API 호출 중 오류 발생');
    }
  };

  // Tarea 데이터 요청 상태값
  const { data, isError, isLoading, refetch } = useQuery([uniqueKey, hCd, sCd, isTunnelInfoUpdated], () => fetchTareaData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    refetchInterval: visibleRefreshBtn ? false : refetchInterval !== undefined ? refetchInterval : false,
  });

  // 새로고침 버튼 클릭 시
  const onClickRefresh = () => {
    refetch();
  };

  if (isError) return <IssueGuide />;

  return (
    <div className='mGas tempEdit'>
      <div className='widget-header margin-top'>
        <div className='widget-title flex-between innerBtn'>
          <span>{t('터널 종합 진행 현황')}</span>
          {visibleRefreshBtn && (
            <>
              <div className='labelSet flex-end'>
                <PlayButton autoPlay={autoPlay} onClick={() => setAutoPlay(!autoPlay)} />
              </div>
              <RefreshButton onClick={onClickRefresh} />
            </>
          )}
        </div>
      </div>
      {!data ? <SkeletonTable /> : <TunnelTable data={data} autoPlay={autoPlay} />}
    </div>
  );
};

export default TunnelTotalTable;
